import { useState, useEffect } from "react";
import { initRemoteConfig, getRemoteConfigBoolean } from "@/firebase/client";

// Track if remote config has been initialized
let isInitialized = false;
let initPromise: Promise<void> | null = null;

/**
 * Hook to get a boolean value from Firebase Remote Config
 * @param key - The remote config key to fetch
 * @param defaultValue - Default value while loading or if fetch fails
 * @returns Object with value and loading state
 */
export function useRemoteConfigBoolean(
  key: string,
  defaultValue: boolean = false
): { value: boolean; loading: boolean } {
  const [value, setValue] = useState<boolean>(defaultValue);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Only initialize once
        if (!isInitialized) {
          if (!initPromise) {
            initPromise = initRemoteConfig();
          }
          await initPromise;
          isInitialized = true;
        }

        // Get the value after initialization
        const configValue = getRemoteConfigBoolean(key);
        setValue(configValue);
      } catch (error) {
        console.error(`Error fetching remote config key "${key}":`, error);
        setValue(defaultValue);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [key, defaultValue]);

  return { value, loading };
}

/**
 * Feature flag keys used in the application
 */
export const RemoteConfigKeys = {
  ALLOW_AFFILIATES: "allow_affiliates",
} as const;
